import { useEffect } from 'react';
import type { Message } from '@copilotkit/shared';
import type { SerializableMessage } from '@/pages/AIChatCopilotKit/types';

type UseCopilotKitHydrationParams = {
  isAvailable: boolean;
  isLoading: boolean;
  messages: Message[];
  reduxMessages: SerializableMessage[];
  setMessages: (messages: Message[]) => void;
  isSyncingRef: React.MutableRefObject<boolean>;
};

/**
 * Hydrates CopilotKit messages from Redux when CopilotKit's in-memory state is empty
 * but Redux has persisted messages.
 *
 * Currently dormant: CopilotKitAgentProvider is mounted at the app level and never
 * unmounts, so CopilotKit messages are never cleared while Redux has data. The welcome
 * message on fresh load is handled separately in useCopilotKitAgent.
 *
 * This activates once Redux is backed by sessionStorage — on page refresh, CopilotKit
 * starts empty while sessionStorage retains the conversation, and this hook restores it.
 *
 * TODO: Wire up redux-persist (or equivalent) with sessionStorage to activate
 * this restoration path.
 */
export const useCopilotKitHydration = ({
  isAvailable,
  isLoading,
  messages,
  reduxMessages,
  setMessages,
  isSyncingRef,
}: UseCopilotKitHydrationParams) => {
  useEffect(() => {
    // Only act when CopilotKit is available and not currently syncing to Redux
    if (!isAvailable || isSyncingRef.current || isLoading) {
      return;
    }

    // Restore from Redux when CopilotKit messages are empty but Redux has them.
    // This is the page-refresh recovery path — activates when Redux is backed by
    // sessionStorage.
    if (messages.length === 0 && reduxMessages.length > 0) {
      setMessages(reduxMessages as Message[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAvailable,
    messages.length,
    reduxMessages.length, // Only length to avoid re-runs on every Redux update
    setMessages,
    isLoading,
    isSyncingRef,
  ]);
};
